<template>
  <div>
    <Breadcrumb :items="breadCrumbItems" :show-transparent-background="false" />
    <ShopProductDetailHead
      v-if="
        $globalPageSettings?.pdpConfiguration && urlKey && $shopStore && locale
      "
      :generate-pdp-link="generatePdpLink"
      :company-name="$globalPageSettings?.companyName"
      :additional-tab-content="additionalTabContent"
      :product-enrichments="productEnrichments"
    >
      <template #contentSlotAboveTabs>
        <LibErrorBoundary>
          <LazyVisibleLibProductEnrichmentAboveTabSection
            :product-enrichments="productEnrichments"
            hydrate-on-visible
          />
        </LibErrorBoundary>
      </template>
      <template #contentSlotCenter>
        <LibErrorBoundary>
          <LazyVisibleLibProductEnrichmentCenterSection
            hydrate-on-visible
            :product-enrichments="productEnrichments"
          />
        </LibErrorBoundary>
      </template>
      <template #contentSlotTabElement>
        <LibErrorBoundary>
          <LibProductEnrichmentTabElement
            :product-enrichments="productEnrichments"
          />
        </LibErrorBoundary>
      </template>
    </ShopProductDetailHead>

    <LibErrorBoundary>
      <LazyVisibleLibProductEnrichmentFooterSection
        hydrate-on-visible
        :product-enrichments="productEnrichments"
      />
    </LibErrorBoundary>
  </div>
</template>

<script setup lang="ts">
import { Breadcrumb } from '@hypercodestudio/basler-components';
import type { NavigationItemInterface } from '@hypercodestudio/basler-components/dist/models/navigation/NavigationItem';
import { StatusCodes } from 'http-status-codes';
import { buildUrlString } from '~/utils/buildUrlString';
import { buildPdpUrl } from '~/utils/buildPdpUrl';
import ProductDetailDistributors from '~/components/shop/ProductDetailDistributors.vue';
import { useSeoAlternateUrls } from '~/composables/useSeoAlternateUrls';
import { useProductEnrichments } from '~/composables/useProductEnrichments';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { useHostname } from '~/composables/useHostname';
import { useProductAddonsStore } from '~/stores/productDetailPageStore/productAddonsStore';
import { isDefined } from '~/utils/guards/isDefined';
import type { BreadcrumbList, ListItem, WithContext } from 'schema-dts';
import { INJECTED_COLOR_MODE } from '~/lib/constants';

const route = useRoute();
const urlKey = computed(() => String(route.params.urlKey));
const locale = useRouteParamLocale();
const logger = useLogger();
const {
  $globalPageSettings,
  $colorMode,
  $localeSlugMap,
  $availableLocales,
  $shopStore,
  $textDictionary,
  $resolvedLocale,
  $secondaryNavigation
} = useNuxtApp();
const serverUrlOrigin = useHostname();

// resolved locale does not include fallback locale.
// pdp pages should return an 404 if no valid locale is given.
// @see https://gcp.baslerweb.com/jira/browse/WEB2-2478
if (!$resolvedLocale.value) {
  throw createError({
    statusCode: StatusCodes.NOT_FOUND,
    fatal: true
  });
}

useSeoAlternateUrls();

const initialColorMode = ref('light');
onMounted(() => {
  $colorMode.value = 'light';
  provide(INJECTED_COLOR_MODE, readonly(initialColorMode));
  $secondaryNavigation.value = null;
});

const productStore = useProductDetailPageStore();
const vscPageLink = computed(() =>
  $globalPageSettings.value?.vscPage?.slug
    ? buildLinkInterface(
        buildUrlString(locale.value, $globalPageSettings.value.vscPage.slug),
        locale.value
      )
    : null
);

watchEffect(() =>
  productStore.$patch(() => {
    // TODO: remove and use {@link usePdpConfiguration}
    productStore.specificationConfiguration = {
      ...$globalPageSettings.value?.pdpConfiguration,
      ...$globalPageSettings.value?.pdpConfigurationVariables
    };
    productStore.urlKey = urlKey.value;
    productStore.vscPageLink = vscPageLink.value;
  })
);

function handleError(error: unknown) {
  if (
    error &&
    typeof error === 'object' &&
    'statusCode' in error &&
    typeof error.statusCode === 'number'
  ) {
    const productOverviewPageLink = useBuildLinkInterface(
      $globalPageSettings.value?.productOverviewPage
    );
    if (
      productOverviewPageLink?.uri &&
      error.statusCode === StatusCodes.NOT_FOUND
    ) {
      return navigateTo(productOverviewPageLink.uri, {
        redirectCode: StatusCodes.MOVED_TEMPORARILY
      });
    }

    throw createError({
      statusCode: error.statusCode,
      cause: error,
      fatal: true
    });
  }

  throw createError({
    statusCode: StatusCodes.INTERNAL_SERVER_ERROR,
    cause: error,
    fatal: true
  });
}

const productAddonStore = useProductAddonsStore();

try {
  await productStore.init(route.query);
} catch (error) {
  handleError(error);
}

watch(
  () => productAddonStore.selectedRequiredItemUrlKey,
  async () => {
    // XXX: this is a workaround for bundle products as the bundle
    // product does not provide suffucient information.
    // Therefore, we load the product from the first option
    // @see https://gcp.baslerweb.com/jira/browse/DBP-448
    if (productAddonStore.selectedRequiredItemUrlKey) {
      await productStore.loadProduct(
        productAddonStore.selectedRequiredItemUrlKey
      );
      productAddonStore.ensureFirstRequiredItem();
    }
  },
  { immediate: true }
);

watch(
  () => productStore.redirect?.urlKey,
  async () => {
    if (productStore.redirect) {
      try {
        await enhancedNavigateTo(
          {
            path: buildPdpUrl(locale.value, productStore.redirect.urlKey),
            query: route.query
          },
          {
            redirectCode:
              productStore.redirect.statusCode ??
              StatusCodes.TEMPORARY_REDIRECT,
            external: false,
            replace: true
          }
        );
      } catch (e) {
        logger.error('failed to redirect on pdp with magento redirect', e);
      }
    }
  },
  { immediate: true }
);

watch(
  () => productStore.isInvalidConfiguration,
  async () => {
    if (productStore.isInvalidConfiguration) {
      try {
        await enhancedNavigateTo(
          {
            path: buildPdpUrl(locale.value, urlKey.value),
            query: productStore.nextValidConfiguration
          },
          {
            redirectCode: StatusCodes.TEMPORARY_REDIRECT,
            external: false,
            replace: true
          }
        );
      } catch (e) {
        logger.error('failed to redirect on pdp with invalid configuration', e);
      }
    }
  },
  { immediate: true }
);

const productEnrichments = await useProductEnrichments();

const additionalTabContent = computed(() => [
  {
    id: 'tab-sources-of-supply',
    title:
      $textDictionary.value['pdp.sources-of-supply.tabPanel.title.label'] ??
      'Sources of supply',
    component: ProductDetailDistributors,
    visible: Boolean(
      productStore.decoratedProduct?.sku !== '' &&
        productStore.decoratedProduct?.distributorsData
    )
  }
]);

const breadCrumbItems = computed(() => {
  const options: NavigationItemInterface[] = [];

  options.push({
    text: 'Home',
    link: {
      uri: buildUrlString(locale.value, undefined, undefined, undefined, true)
    }
  });
  breadCrumbCategoryItems.value && breadCrumbCategoryItems?.value?.length
    ? breadCrumbCategoryItems.value.forEach((entry) => {
        options.push(entry);
      })
    : options.push({
        text:
          $textDictionary.value[
            'globalPageSettings.productOverviewPage.label'
          ] ?? 'Products',
        link: $globalPageSettings.value?.productOverviewPage?.slug
          ? {
              uri: buildUrlString(
                locale.value,
                $globalPageSettings.value?.productOverviewPage.slug,
                undefined,
                undefined,
                true
              )
            }
          : {}
      });

  options.push({
    text: productStore.decoratedProduct?.name || urlKey.value || '',
    link: { uri: buildPdpUrl(locale.value, urlKey.value, true) } // XXX: We have to set this field to undefined instead of removing it because without it the text is not shown
  });

  return options;
});

const { data: breadCrumbCategoryItems } = await useAsyncData<
  NavigationItemInterface[]
>(async () => {
  const entries = await Promise.all(
    productStore.decoratedProduct?.categories?.map(async (link) => {
      try {
        const response = await $fetch(
          `/api/contentful/slugById/${link?.contentful_page_id}?locale=${locale.value}`
        );

        return {
          text: link?.breadcrumb_name ?? link?.name ?? '',
          link: {
            uri: buildUrlString(
              locale.value,
              response.slug,
              undefined,
              undefined,
              true
            )
          }
        };
      } catch (e) {
        logger.error(
          'failed to retrieve following breadcrumb category items',
          e
        );
      }
    }) ?? []
  );

  return entries.filter(isDefined);
});

watch(
  // do not use active product here, as a configured product might
  // have a different url key than the initial (unconfigured) product
  () => productStore.product?.url_key,
  () => {
    const urlKey = productStore.product?.url_key;
    if (!urlKey) {
      $localeSlugMap.value = {};

      return;
    }

    $localeSlugMap.value = $availableLocales.value?.reduce(
      (collector, code) => {
        collector[code] = buildPdpUrl('', urlKey, false);

        return collector;
      },
      {} as Record<string, string>
    );
  },
  { immediate: true }
);

// Currently a separate function as this is used to create metadata
// which requires an absolute url.
function generatePdpLink(locale: string, p: DecoratedProduct) {
  return buildPdpUrl(locale, p.urlKey!, serverUrlOrigin);
}

const breadcrumbList = computed<WithContext<BreadcrumbList>>(() => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadCrumbItems.value.map<ListItem>((item, index) => ({
    '@type': 'ListItem',
    name: item.text,
    position: index + 1,
    item: typeof item.link?.uri === 'string' ? item.link?.uri : ''
  }))
}));

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(breadcrumbList.value),
      tagPriority: -1 // Optional: Sicherstellen, dass es zuletzt eingefügt wird
    }
  ]
});
</script>
